import axios from 'axios';
import {
  Project,
  CSVJobStatus,
  CustomField,
  BatchResults,
  ChatRequest,
  ChatResponse,
  StructuredChatRequest,
  StructuredChatResponse,
  DocumentListResponse,
  DocumentMetadataUpdate,
  URLMappingResponse,
  QADatabaseInfo,
  UploadResult
} from '../types';
import config from '../config';
import { supabase } from './supabase';

const api = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token to all requests
api.interceptors.request.use(
  async (config) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        config.headers.Authorization = `Bearer ${session.access_token}`;
      }
    } catch (error) {
      console.warn('Failed to get auth session for API request:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors (expired tokens)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token might be expired, try to refresh
      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        // Refresh failed - user needs to log in again
        console.error('Session refresh failed:', refreshError);
        // Could emit an event or redirect to login here
      }
    }
    return Promise.reject(error);
  }
);

// Project Settings Type
export interface ProjectSettings {
  project_id: string;
  company_name: string;
  description: string | null;
  response_verbosity: string;
  always_include_policy_context: boolean;
}

export interface ProjectSettingsUpdate {
  company_name: string;
  description: string | null;
  response_verbosity: string;
  always_include_policy_context: boolean;
}

export const healthCheck = async (): Promise<{ status: string; service: string }> => {
  const response = await api.get('/api/health');
  return response.data;
};

export const getProjects = async (): Promise<Project[]> => {
  const response = await api.get('/api/projects');
  return response.data;
};

export const getProject = async (projectId: string): Promise<Project> => {
  const response = await api.get(`/api/projects/${projectId}`);
  return response.data;
};

export const uploadCSVBatch = async (
  file: File,
  projectId: string,
  customFields: CustomField[] = []
): Promise<{ job_id: string; message: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('project_id', projectId);
  formData.append('custom_fields', JSON.stringify(customFields));

  const response = await api.post('/api/csv-batch', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getCSVJobStatus = async (jobId: string): Promise<CSVJobStatus> => {
  const response = await api.get(`/api/csv-batch/${jobId}`);
  return response.data;
};

export const getBatchResults = async (jobId: string): Promise<BatchResults> => {
  const response = await api.get(`/api/csv-batch/${jobId}/results`);
  return response.data;
};

// Project Settings API
export const getProjectSettings = async (projectId: string): Promise<ProjectSettings> => {
  const response = await api.get(`/api/projects/${projectId}/settings`);
  return response.data;
};

export const updateProjectSettings = async (
  projectId: string,
  settings: ProjectSettingsUpdate
): Promise<ProjectSettings> => {
  const response = await api.put(`/api/projects/${projectId}/settings`, settings);
  return response.data;
};

// BYOK (Bring Your Own Key) Types
export interface BYOKSettings {
  project_id: string;
  byok_enabled: boolean;
  has_api_key: boolean;
}

export interface BYOKSettingsUpdate {
  byok_enabled: boolean;
  gemini_api_key?: string;
}

// BYOK API Functions
export const getBYOKSettings = async (projectId: string): Promise<BYOKSettings> => {
  const response = await api.get(`/api/projects/${projectId}/byok`);
  return response.data;
};

export const updateBYOKSettings = async (
  projectId: string,
  settings: BYOKSettingsUpdate
): Promise<BYOKSettings> => {
  const response = await api.put(`/api/projects/${projectId}/byok`, settings);
  return response.data;
};

// Chat API functions
export const sendChatMessage = async (
  message: string,
  projectId: string,
  includeDebug: boolean = false
): Promise<ChatResponse> => {
  const request: ChatRequest = { message, project_id: projectId, include_debug: includeDebug };
  const response = await api.post('/api/chat', request);
  return response.data;
};

// Regenerate response with additional context
export const regenerateResponseWithContext = async (
  question: string,
  additionalContext: string,
  projectId: string,
  includeDebug: boolean = true
): Promise<ChatResponse> => {
  // Combine question with additional context in the message
  const enhancedMessage = `${question}\n\nAdditional Context: ${additionalContext}`;
  const request: ChatRequest = { message: enhancedMessage, project_id: projectId, include_debug: includeDebug };
  const response = await api.post('/api/chat', request);
  return response.data;
};

export const sendStructuredChatMessage = async (
  question: string,
  projectId: string,
  customFields: CustomField[]
): Promise<StructuredChatResponse> => {
  const request: StructuredChatRequest = {
    question,
    project_id: projectId,
    custom_fields: customFields
  };
  const response = await api.post('/api/chat-structured', request);
  return response.data;
};

export const getCSVDownloadUrl = (jobId: string): string => {
  return `${config.apiUrl}/api/csv-batch/${jobId}/download`;
};

// === Knowledge Base API ===

export const listDocuments = async (projectId: string): Promise<DocumentListResponse> => {
  const response = await api.get(`/api/knowledge-base/${projectId}/documents`);
  return response.data;
};

export const uploadDocuments = async (
  projectId: string,
  files: File[],
  replaceExisting: boolean = false
): Promise<UploadResult> => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  const response = await api.post(
    `/api/knowledge-base/${projectId}/documents/upload?replace_existing=${replaceExisting}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

export const updateDocumentMetadata = async (
  projectId: string,
  filename: string,
  update: DocumentMetadataUpdate
): Promise<{ filename: string; display_name: string; public_url: string | null }> => {
  const response = await api.put(
    `/api/knowledge-base/${projectId}/documents/${encodeURIComponent(filename)}/metadata`,
    update
  );
  return response.data;
};

export const deleteDocument = async (
  projectId: string,
  filename: string
): Promise<{ project_id: string; filename: string; deleted: boolean }> => {
  const response = await api.delete(
    `/api/knowledge-base/${projectId}/documents/${encodeURIComponent(filename)}`
  );
  return response.data;
};

export const checkDocumentsExist = async (
  projectId: string,
  filenames: string[]
): Promise<{ project_id: string; existing_files: string[]; count: number }> => {
  const response = await api.post(
    `/api/knowledge-base/${projectId}/documents/check-exists`,
    filenames
  );
  return response.data;
};

export const getQADatabase = async (projectId: string): Promise<QADatabaseInfo> => {
  const response = await api.get(`/api/knowledge-base/${projectId}/qa-database`);
  return response.data;
};

export const uploadQADatabase = async (
  projectId: string,
  file: File
): Promise<{ project_id: string; filename: string; record_count: number; message: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(
    `/api/knowledge-base/${projectId}/qa-database/upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

export const uploadURLMappings = async (
  projectId: string,
  file: File
): Promise<URLMappingResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(
    `/api/knowledge-base/${projectId}/url-mappings/upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

export const saveURLMappings = async (
  projectId: string,
  mappings: Array<{ filename: string; display_name: string; public_url: string }>
): Promise<{ project_id: string; saved_count: number; message: string }> => {
  const response = await api.post(`/api/knowledge-base/${projectId}/url-mappings/save`, {
    mappings,
  });
  return response.data;
};

export const getURLMappings = async (
  projectId: string
): Promise<{ project_id: string; has_mappings: boolean; mappings: Array<{ filename: string; public_url: string }> }> => {
  const response = await api.get(`/api/knowledge-base/${projectId}/url-mappings`);
  return response.data;
};

export interface AutoMapSuggestion {
  csv_name: string;
  public_url: string;
  matched_file: string | null;
  confidence: number;
  auto_mapped: boolean;
  already_saved: boolean;
}

export interface AutoMapResponse {
  project_id: string;
  has_url_csv: boolean;
  suggestions: AutoMapSuggestion[];
  unmapped_documents: string[];
  auto_mapped_count?: number;
  needs_manual_count?: number;
  message?: string;
}

export const autoMapURLs = async (projectId: string): Promise<AutoMapResponse> => {
  const response = await api.post(`/api/knowledge-base/${projectId}/url-mappings/auto-map`);
  return response.data;
};

export const clearURLMappings = async (projectId: string): Promise<{ cleared_count: number; message: string }> => {
  const response = await api.delete(`/api/knowledge-base/${projectId}/url-mappings`);
  return response.data;
};

// === Q&A Feedback Loop API ===

export interface QAUpdateEntry {
  question: string;
  answer: string;
}

export interface QAUpdateResponse {
  project_id: string;
  added_count: number;
  total_count: number;
  vector_indexed: boolean;
  message: string;
}

export const updateQADatabaseFromResponses = async (
  projectId: string,
  entries: QAUpdateEntry[],
  customerName?: string
): Promise<QAUpdateResponse> => {
  const response = await api.post(
    `/api/knowledge-base/${projectId}/qa-database/update-from-responses`,
    { entries, customer_name: customerName }
  );
  return response.data;
};

// === Source Refresh API ===

export interface RefreshSourcesRequest {
  project_id: string;
  response_text: string;
  question?: string;
}

export interface RefreshSourcesResponse {
  sources: Array<{ doc_name: string; url?: string }>;
  matched_count: number;
  total_available: number;
}

/**
 * Refresh sources by matching response text against available policy documents.
 * Uses LLM to identify which documents are referenced in the response.
 */
export const refreshSources = async (
  projectId: string,
  responseText: string,
  question?: string
): Promise<RefreshSourcesResponse> => {
  const request: RefreshSourcesRequest = {
    project_id: projectId,
    response_text: responseText,
    question
  };
  const response = await api.post('/api/refresh-sources', request);
  return response.data;
};

export default api;

